import React, { useEffect, useState, useCallback } from 'react';
import { useCompanyStore } from '../../stores/useCompanyStore';
import { useGlobalDataRefresh } from '../../hooks/useCompanyDataSync';
import { useCompanies } from '../../hooks/queries/useCompanies';
import { logger } from '@/lib/logger';

interface CompanyOption {
  id: number;
  name: string;
  [key: string]: unknown;
}

const CompanySelector: React.FC = () => {
  // Get companies from React Query (server state)
  const { companies, isLoading, error } = useCompanies() as {
    companies: CompanyOption[];
    isLoading: boolean;
    error: unknown;
  };
  // Get UI state from Zustand store
  const {
    activeCompany,
    setActiveCompanyById,
  } = useCompanyStore();

  const [isChanging, setIsChanging] = useState<boolean>(false);
  const [changeStartTime, setChangeStartTime] = useState<number | null>(null);
  const { refreshAllData } = useGlobalDataRefresh();

  // NOTE: Companies are now loaded automatically by React Query
  // No manual loading needed - React Query handles it

  // Timer para ocultar el indicador de cambio
  useEffect(() => {
    if (isChanging && changeStartTime) {
      const elapsed = Date.now() - changeStartTime;
      const remainingTime = Math.max(0, 3000 - elapsed); // 3 segundos total

      const timer = setTimeout(() => {
        setIsChanging(false);
        setChangeStartTime(null);
      }, remainingTime);

      return () => clearTimeout(timer);
    }
  }, [isChanging, changeStartTime]);

  const handleCompanyChange = useCallback(
    async (event: React.ChangeEvent<HTMLSelectElement>) => {
      const companyId = parseInt(event.target.value, 10);
      const selectedCompany = companies.find((c) => c.id === companyId);

      if (selectedCompany && selectedCompany.id !== activeCompany?.id) {
        setIsChanging(true);
        setChangeStartTime(Date.now());

        try {
          // Cambiar empresa activa - pass companies array from React Query
          const result = setActiveCompanyById(companyId, companies);

          if (result) {
            // Forzar actualización global de todos los datos
            refreshAllData();
          }
        } catch (err) {
          logger.error('Error cambiando empresa:', err);
          setIsChanging(false);
          setChangeStartTime(null);
        }
      }
    },
    [companies, activeCompany?.id, setActiveCompanyById, refreshAllData],
  );

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
        <span className="text-sm text-gray-500">Cargando empresas...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-600">
        Error: {error instanceof Error ? error.message : String(error)}
      </div>
    );
  }

  if (companies.length === 0 && !isLoading) {
    return (
      <div className="text-sm text-gray-500">
        No tienes empresas
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      <label htmlFor="company-selector" className="text-sm font-medium text-gray-700">
        Empresa:
      </label>
      <div className="relative">
        <select
          id="company-selector"
          value={activeCompany?.id || ''}
          onChange={handleCompanyChange}
          disabled={isChanging}
          className={`block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white ${
            isChanging ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.name}
            </option>
          ))}
        </select>
        {isChanging && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-md">
            <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
        )}
      </div>
      {isChanging && (
        <span className="text-xs text-blue-600 animate-pulse">
          Actualizando...
        </span>
      )}
    </div>
  );
};

export default CompanySelector;
